import Tesseract from 'tesseract.js';
import { parse as parseMrz } from 'mrz';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

/* ── ICAO 3-letter → ISO 2-letter country code ───────────────────────────── */
const MRZ_TO_ISO2 = {
  AFG: 'AF', ALB: 'AL', DZA: 'DZ', AND: 'AD', AGO: 'AO', ARG: 'AR',
  ARM: 'AM', AUS: 'AU', AUT: 'AT', AZE: 'AZ', BHS: 'BS', BHR: 'BH',
  BGD: 'BD', BLR: 'BY', BEL: 'BE', BLZ: 'BZ', BEN: 'BJ', BTN: 'BT',
  BOL: 'BO', BIH: 'BA', BWA: 'BW', BRA: 'BR', BRN: 'BN', BGR: 'BG',
  BFA: 'BF', BDI: 'BI', KHM: 'KH', CMR: 'CM', CAN: 'CA', CAF: 'CF',
  TCD: 'TD', CHL: 'CL', CHN: 'CN', COL: 'CO', COD: 'CD', COG: 'CG',
  CRI: 'CR', HRV: 'HR', CUB: 'CU', CYP: 'CY', CZE: 'CZ', DNK: 'DK',
  DJI: 'DJ', DOM: 'DO', ECU: 'EC', EGY: 'EG', SLV: 'SV', GNQ: 'GQ',
  ERI: 'ER', EST: 'EE', ETH: 'ET', FJI: 'FJ', FIN: 'FI', FRA: 'FR',
  GAB: 'GA', GMB: 'GM', GEO: 'GE', DEU: 'DE', GHA: 'GH', GRC: 'GR',
  GTM: 'GT', GIN: 'GN', GNB: 'GW', GUY: 'GY', HTI: 'HT', HND: 'HN',
  HUN: 'HU', ISL: 'IS', IND: 'IN', IDN: 'ID', IRN: 'IR', IRQ: 'IQ',
  IRL: 'IE', ISR: 'IL', ITA: 'IT', JAM: 'JM', JPN: 'JP', JOR: 'JO',
  KAZ: 'KZ', KEN: 'KE', PRK: 'KP', KOR: 'KR', KWT: 'KW', KGZ: 'KG',
  LAO: 'LA', LVA: 'LV', LBN: 'LB', LSO: 'LS', LBR: 'LR', LBY: 'LY',
  LIE: 'LI', LTU: 'LT', LUX: 'LU', MDG: 'MG', MWI: 'MW', MYS: 'MY',
  MDV: 'MV', MLI: 'ML', MLT: 'MT', MRT: 'MR', MUS: 'MU', MEX: 'MX',
  MDA: 'MD', MCO: 'MC', MNG: 'MN', MNE: 'ME', MAR: 'MA', MOZ: 'MZ',
  MMR: 'MM', NAM: 'NA', NPL: 'NP', NLD: 'NL', NZL: 'NZ', NIC: 'NI',
  NER: 'NE', NGA: 'NG', MKD: 'MK', NOR: 'NO', OMN: 'OM', PAK: 'PK',
  PAN: 'PA', PNG: 'PG', PRY: 'PY', PER: 'PE', PHL: 'PH', POL: 'PL',
  PRT: 'PT', QAT: 'QA', ROU: 'RO', RUS: 'RU', RWA: 'RW', SAU: 'SA',
  SEN: 'SN', SRB: 'RS', SLE: 'SL', SGP: 'SG', SVK: 'SK', SVN: 'SI',
  SOM: 'SO', ZAF: 'ZA', ESP: 'ES', LKA: 'LK', SDN: 'SD', SWE: 'SE',
  CHE: 'CH', SYR: 'SY', TWN: 'TW', TJK: 'TJ', TZA: 'TZ', THA: 'TH',
  TLS: 'TL', TGO: 'TG', TTO: 'TT', TUN: 'TN', TUR: 'TR', TKM: 'TM',
  UGA: 'UG', UKR: 'UA', ARE: 'AE', GBR: 'GB', USA: 'US', URY: 'UY',
  UZB: 'UZ', VEN: 'VE', VNM: 'VN', YEM: 'YE', ZMB: 'ZM', ZWE: 'ZW',
  D: 'DE', UTO: null,
};

/* ── GCC countries for Arabic OCR ───────────────────────────────────────── */
const GCC_COUNTRIES = new Set(['AE', 'SA', 'QA', 'KW', 'BH', 'OM']);
const ARABIC_DOC_TYPES = new Set(['NATIONAL_ID', 'PROOF_OF_ADDRESS']);

/* ── date helpers ────────────────────────────────────────────────────────── */
const MONTH_MAP = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

function mrzDateToInput(yymmdd) {
  if (!yymmdd || yymmdd.length !== 6 || /[^0-9]/.test(yymmdd)) return '';
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = yymmdd.slice(2, 4);
  const dd = yymmdd.slice(4, 6);
  const yyyy = yy < 50 ? `20${String(yy).padStart(2, '0')}` : `19${String(yy).padStart(2, '0')}`;
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Comprehensive date normalizer.
 * Searches within `raw` text for the first recognizable date.
 * Handles: "15 Jan 2024", "15 January 2024", "15/01/2024", "01/15/2024",
 *           "2024-01-15", "JAN 2024", "2024 JAN 15"
 * Returns: "YYYY-MM-DD" or null
 */
export function normalizeDate(raw) {
  if (!raw) return null;
  const upper = raw.toUpperCase();

  // "15 Jan 2024" or "15 January 2024"
  const m1 = upper.match(
    /(\d{1,2})\s+(JAN(?:UARY)?|FEB(?:RUARY)?|MAR(?:CH)?|APR(?:IL)?|MAY|JUN(?:E)?|JUL(?:Y)?|AUG(?:UST)?|SEP(?:TEMBER)?|OCT(?:OBER)?|NOV(?:EMBER)?|DEC(?:EMBER)?)\s+(\d{4})/
  );
  if (m1) {
    const [, d, mon, y] = m1;
    return `${y}-${MONTH_MAP[mon.slice(0, 3)]}-${d.padStart(2, '0')}`;
  }

  // "DD/MM/YYYY" (default) or "MM/DD/YYYY" (when second part > 12)
  const m2 = upper.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m2) {
    const [, a, b, y] = m2;
    if (parseInt(b, 10) > 12) {
      // second part is a day → MM/DD/YYYY
      return `${y}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`;
    }
    // assume DD/MM/YYYY (UAE / European convention)
    return `${y}-${b.padStart(2, '0')}-${a.padStart(2, '0')}`;
  }

  // "2024-01-15" (ISO)
  const m3 = upper.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m3) {
    const [, y, mo, d] = m3;
    return `${y}-${mo}-${d}`;
  }

  // "2024 JAN 15"
  const m4 = upper.match(/(\d{4})\s+(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[A-Z]*\s+(\d{1,2})/);
  if (m4) {
    const [, y, mon, d] = m4;
    return `${y}-${MONTH_MAP[mon]}-${d.padStart(2, '0')}`;
  }

  // "JAN 2024" (month + year only → first of month)
  const m5 = upper.match(/(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[A-Z]*\s+(\d{4})/);
  if (m5) {
    const [, mon, y] = m5;
    return `${y}-${MONTH_MAP[mon]}-01`;
  }

  return null;
}

/* ── image preprocessing helpers ────────────────────────────────────────── */
function grayscale(data) {
  for (let i = 0; i < data.length; i += 4) {
    const g = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    data[i] = data[i + 1] = data[i + 2] = g;
  }
}

function pixelMean(data) {
  let sum = 0;
  const count = data.length / 4;
  for (let i = 0; i < data.length; i += 4) sum += data[i];
  return count > 0 ? sum / count : 128;
}

function invertPixels(data) {
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i];
    data[i + 1] = 255 - data[i + 1];
    data[i + 2] = 255 - data[i + 2];
  }
}

function otsuThreshold(data, pixelCount) {
  const hist = new Int32Array(256);
  for (let i = 0; i < data.length; i += 4) hist[data[i]]++;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, maxVar = 0, threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = pixelCount - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const variance = wB * wF * (mB - mF) ** 2;
    if (variance > maxVar) { maxVar = variance; threshold = t; }
  }
  return threshold;
}

function binarize(data, threshold) {
  for (let i = 0; i < data.length; i += 4) {
    const v = data[i] < threshold ? 0 : 255;
    data[i] = data[i + 1] = data[i + 2] = v;
  }
}

function morphologicalClose(data, width, height) {
  const count = width * height;
  const bin = new Uint8Array(count);
  for (let i = 0; i < count; i++) bin[i] = data[i * 4] > 127 ? 1 : 0;

  // Dilate: expand black (0) pixels by 1px radius
  const dil = new Uint8Array(count).fill(1);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (bin[y * width + x] === 0) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const ny = y + dy, nx = x + dx;
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) dil[ny * width + nx] = 0;
          }
        }
      }
    }
  }

  // Erode: keep black only when entire 3×3 neighbourhood in dilated image is black
  const out = new Uint8ClampedArray(count * 4).fill(255);
  for (let i = 3; i < out.length; i += 4) out[i] = 255;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (dil[y * width + x] !== 0) continue;
      let keep = true;
      for (let dy = -1; dy <= 1 && keep; dy++) {
        for (let dx = -1; dx <= 1 && keep; dx++) {
          const ny = y + dy, nx = x + dx;
          if (ny < 0 || ny >= height || nx < 0 || nx >= width || dil[ny * width + nx] !== 0) keep = false;
        }
      }
      if (keep) {
        const idx = (y * width + x) * 4;
        out[idx] = out[idx + 1] = out[idx + 2] = 0;
      }
    }
  }
  return out;
}

function detectSkewAngle(data, width, height) {
  const count = width * height;
  const bin = new Uint8Array(count);
  for (let i = 0; i < count; i++) bin[i] = data[i * 4] < 128 ? 1 : 0;

  let bestAngle = 0, bestVar = -Infinity;
  for (let deg = -15; deg <= 15; deg++) {
    const rad = (deg * Math.PI) / 180;
    const cosA = Math.cos(rad), sinA = Math.sin(rad);
    const proj = new Float32Array(height);
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (bin[y * width + x]) {
          const py = Math.round(x * sinA + y * cosA);
          if (py >= 0 && py < height) proj[py]++;
        }
      }
    }
    let s = 0;
    for (let i = 0; i < height; i++) s += proj[i];
    const mean = s / height;
    let v = 0;
    for (let i = 0; i < height; i++) v += (proj[i] - mean) ** 2;
    if (v > bestVar) { bestVar = v; bestAngle = deg; }
  }
  return bestAngle;
}

function applyPreprocessingPipeline(canvas, ctx, width, height) {
  if (!ctx || width < 2 || height < 2) return canvas;

  let imageData = ctx.getImageData(0, 0, width, height);

  // a) Grayscale (luminance formula)
  grayscale(imageData.data);

  // b) Invert if very dark (mean < 80) before thresholding
  if (pixelMean(imageData.data) < 80) invertPixels(imageData.data);

  // c) Otsu adaptive threshold → binarize
  const thresh = otsuThreshold(imageData.data, width * height);
  binarize(imageData.data, thresh);
  ctx.putImageData(imageData, 0, 0);

  // d) Morphological closing (dilate → erode) to fill small text holes
  const closedData = morphologicalClose(ctx.getImageData(0, 0, width, height).data, width, height);
  ctx.putImageData(new ImageData(closedData, width, height), 0, 0);

  // e) Deskew via projection-profile method (Hough-like, ±15°)
  const angle = detectSkewAngle(ctx.getImageData(0, 0, width, height).data, width, height);
  if (Math.abs(angle) >= 0.5) {
    const deskewed = document.createElement('canvas');
    deskewed.width = width;
    deskewed.height = height;
    const dCtx = deskewed.getContext('2d');
    dCtx.fillStyle = 'white';
    dCtx.fillRect(0, 0, width, height);
    dCtx.translate(width / 2, height / 2);
    dCtx.rotate((-angle * Math.PI) / 180);
    dCtx.translate(-width / 2, -height / 2);
    dCtx.drawImage(canvas, 0, 0);
    return deskewed;
  }

  return canvas;
}

/* ── image preprocessing ─────────────────────────────────────────────────── */
export async function imagePreprocess(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const w = img.naturalWidth || 1;
        const h = img.naturalHeight || 1;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        const result = applyPreprocessingPipeline(canvas, ctx, w, h);
        resolve({ canvas: result, dataUrl: result.toDataURL('image/png') });
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for preprocessing'));
    };
    img.src = url;
  });
}

/* ── PDF preprocessing ───────────────────────────────────────────────────── */
async function renderPageToCanvas(page, scale = 2.0) {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d');
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas;
}

function preprocessCanvas(canvas) {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  const result = applyPreprocessingPipeline(canvas, ctx, w, h);
  return { canvas: result, dataUrl: result.toDataURL('image/png') };
}

/* ── canvas crop helper ──────────────────────────────────────────────────── */
export function cropCanvas(canvas, x, y, w, h) {
  const cropped = document.createElement('canvas');
  cropped.width = Math.max(1, Math.round(w));
  cropped.height = Math.max(1, Math.round(h));
  const ctx = cropped.getContext('2d');
  ctx.drawImage(
    canvas,
    Math.round(x), Math.round(y), Math.round(w), Math.round(h),
    0, 0, cropped.width, cropped.height
  );
  return cropped;
}

/* ── OCR ─────────────────────────────────────────────────────────────────── */
export async function runOcrWithOptions(imageData, lang = 'eng', ocrOptions = {}, onProgress) {
  const { data } = await Tesseract.recognize(imageData, lang, {
    logger: (m) => {
      if (m.status === 'recognizing text' && onProgress) {
        onProgress(Math.round(m.progress * 100));
      }
    },
    ...ocrOptions,
  });
  return {
    text: data.text,
    confidence: data.confidence / 100,
    words: data.words || [],
  };
}

export async function runOcr(imageData, onProgress) {
  return runOcrWithOptions(imageData, 'eng', {}, onProgress);
}

/* ── document type detection ─────────────────────────────────────────────── */

// Maximum achievable scores per type (used to normalise confidence)
const DOC_MAX_SCORES = {
  PASSPORT: 9,
  NATIONAL_ID: 6,
  BANK_STATEMENT: 7,
  PROOF_OF_ADDRESS: 6,
  TAX_ID: 6,
};

/**
 * Returns { type, confidence, scores } where `type` is the winning class
 * (or null when nothing matched), `confidence` is score/maxScore, and
 * `scores` contains the raw weighted tally for each type.
 */
export function detectDocumentType(text, hintedType = null) {
  if (hintedType) return { type: hintedType, confidence: 1.0, scores: {} };

  const t = text.toUpperCase();
  const scores = { PASSPORT: 0, NATIONAL_ID: 0, BANK_STATEMENT: 0, PROOF_OF_ADDRESS: 0, TAX_ID: 0 };

  // PASSPORT — max 9
  if (/^[A-Z0-9<]{44}$/m.test(t)) scores.PASSPORT += 3;              // MRZ line (44-char)
  if (/PASSPORT/.test(t)) scores.PASSPORT += 2;                       // keyword
  if (/<</.test(t)) scores.PASSPORT += 3;                             // MRZ << filler (unique to MRZ)
  if (/DATE\s+OF\s+ISSUE/.test(t)) scores.PASSPORT += 1;              // field label

  // NATIONAL_ID — max 6
  if (/\b784[-\s]?\d{4}[-\s]?\d{7}[-\s]?\d/.test(t)) scores.NATIONAL_ID += 3; // Emirates ID
  if (/NATIONAL\s*ID|EMIRATES\s*ID|IDENTITY\s*CARD/.test(t)) scores.NATIONAL_ID += 2;
  if (/DRIVER['\s]*S?\s*LICEN[CS]E/.test(t)) scores.NATIONAL_ID += 1;

  // BANK_STATEMENT — max 7
  if (/STATEMENT\s+OF\s+ACCOUNT|ACCOUNT\s+STATEMENT/.test(t)) scores.BANK_STATEMENT += 3;
  if (/\b[A-Z]{2}\d{2}[A-Z0-9]{10,}\b/.test(t)) scores.BANK_STATEMENT += 2;   // IBAN-like
  if (/BALANCE/.test(t)) scores.BANK_STATEMENT += 1;
  if (/STATEMENT\s+(?:DATE|PERIOD)|DATE\s+OF\s+STATEMENT/.test(t)) scores.BANK_STATEMENT += 1;

  // PROOF_OF_ADDRESS — max 6
  if (/ELECTRICITY|WATER\s+BILL|GAS\s+BILL|UTILITY\s+BILL|ETISALAT|DU\s+TELECOM/.test(t)) scores.PROOF_OF_ADDRESS += 3;
  if (/SERVICE\s+ADDRESS|PROOF\s+OF\s+ADDRESS/.test(t)) scores.PROOF_OF_ADDRESS += 2;
  if (/ACCOUNT\s+NO|ACCOUNT\s+NUMBER|A\/C\s+NO/.test(t)) scores.PROOF_OF_ADDRESS += 1;

  // TAX_ID — max 6
  if (/\bFORM\s+1040\b|TAX\s+RETURN/.test(t)) scores.TAX_ID += 3;
  if (/\d{3}-\d{2}-\d{4}/.test(t)) scores.TAX_ID += 2;               // SSN format
  if (/ADJUSTED\s+GROSS\s+INCOME/.test(t)) scores.TAX_ID += 1;

  // Argmax
  let type = null;
  let maxScore = 0;
  for (const [k, v] of Object.entries(scores)) {
    if (v > maxScore) { maxScore = v; type = k; }
  }

  if (maxScore === 0) return { type: null, confidence: 0, scores };

  const confidence = maxScore / (DOC_MAX_SCORES[type] || 1);
  return { type, confidence, scores };
}

/* ── per-field helpers ───────────────────────────────────────────────────── */
function field(value, confidence, source) {
  return { value, confidence, source };
}

/* ── field extraction ────────────────────────────────────────────────────── */

/**
 * @param {string} text   Full OCR text from the document
 * @param {string} mrzText  Optional dedicated MRZ-crop OCR pass (bottom 25%)
 */
function extractPassportFields(text, mrzText = '') {
  const fields = {};

  // Prefer dedicated MRZ OCR result; fall back to full-page OCR
  const mrzLineRe = /^[A-Z0-9<]{44}$/gm;
  let mrzLines = mrzText ? (mrzText.match(mrzLineRe) || []) : [];
  if (mrzLines.length < 2) mrzLines = text.match(mrzLineRe) || [];

  if (mrzLines.length >= 2) {
    try {
      const parsed = parseMrz([mrzLines[0], mrzLines[1]]);
      if (parsed.fields.documentNumber) {
        fields.documentNumber = field(parsed.fields.documentNumber, parsed.valid ? 0.97 : 0.70, 'mrz');
      }
      if (parsed.fields.issuingState) {
        const iso2 = MRZ_TO_ISO2[parsed.fields.issuingState];
        if (iso2) fields.issuingCountry = field(iso2, parsed.valid ? 0.95 : 0.65, 'mrz');
      }
      if (parsed.fields.expirationDate) {
        const converted = mrzDateToInput(parsed.fields.expirationDate);
        if (converted) fields.expiryDate = field(converted, parsed.valid ? 0.95 : 0.65, 'mrz');
      }
    } catch (_e) {
      // MRZ parse failed — fall through to text extraction
    }
  }

  // Fallback text extraction for missing fields
  if (!fields.documentNumber) {
    const numMatch = text.match(/\b([A-Z]{1,2}[0-9]{6,8})\b/);
    if (numMatch) fields.documentNumber = field(numMatch[1], 0.60, 'regex');
  }

  if (!fields.expiryDate) {
    const dateMatch = normalizeDate(text);
    if (dateMatch) fields.expiryDate = field(dateMatch, 0.50, 'regex');
  }

  return { fields };
}

function extractNationalIdFields(text) {
  const fields = {};

  // UAE Emirates ID: 784-YYYY-XXXXXXX-X
  const uaeMatch = text.match(/\b(784[-\s]?\d{4}[-\s]?\d{7}[-\s]?\d)\b/);
  if (uaeMatch) {
    fields.documentNumber = field(uaeMatch[1].replace(/\s/g, ''), 0.90, 'regex');
    fields.issuingCountry = field('AE', 0.95, 'inferred');
  }

  if (!fields.documentNumber) {
    const generic = text.match(/\b(\d{9,15})\b/);
    if (generic) fields.documentNumber = field(generic[1], 0.55, 'regex');
  }

  const dateMatch = normalizeDate(text);
  if (dateMatch) fields.expiryDate = field(dateMatch, 0.55, 'regex');

  return { fields };
}

function extractBankStatementFields(text) {
  const fields = {};
  const upper = text.toUpperCase();

  // UAE IBAN preferred (regex per spec: AE + 2 check + 4 alphanum + 16 digits)
  const uaeIban = upper.match(/\bAE\d{2}[0-9A-Z]{4}\d{16}\b/);
  if (uaeIban) {
    fields.documentNumber = field(uaeIban[0], 0.92, 'regex');
  } else {
    const genericIban = upper.match(/\b([A-Z]{2}\d{2}[A-Z0-9]{4,})\b/);
    if (genericIban) fields.documentNumber = field(genericIban[1], 0.80, 'regex');
  }

  // Statement period: "01 Jan 2024 - 31 Jan 2024" → issueDate + expiryDate
  const periodRe = /(\d{1,2}[\s/\-]+[A-Za-z]+[\s/\-]+\d{4})\s*[-–—]|to\s+(\d{1,2}[\s/\-]+[A-Za-z]+[\s/\-]+\d{4})/i;
  const periodFull = /(\d{1,2}[\s/\-]+[A-Za-z]+[\s/\-]+\d{4})\s*[-–—to]+\s*(\d{1,2}[\s/\-]+[A-Za-z]+[\s/\-]+\d{4})/i;
  const periodMatch = text.match(periodFull);
  if (periodMatch) {
    const startDate = normalizeDate(periodMatch[1]);
    const endDate = normalizeDate(periodMatch[2]);
    if (startDate) fields.issueDate = field(startDate, 0.75, 'regex');
    if (endDate) fields.expiryDate = field(endDate, 0.75, 'regex');
  } else {
    const dateMatch = normalizeDate(text);
    if (dateMatch) fields.issueDate = field(dateMatch, 0.70, 'regex');
  }

  return { fields };
}

function extractProofOfAddressFields(text) {
  const fields = {};

  const dateMatch = normalizeDate(text);
  if (dateMatch) fields.issueDate = field(dateMatch, 0.65, 'regex');

  const upper = text.toUpperCase();
  if (/UNITED ARAB EMIRATES|DUBAI|ABU DHABI|SHARJAH|ETISALAT|DU\s+TELECOM/.test(upper)) {
    fields.issuingCountry = field('AE', 0.75, 'inferred');
  } else if (/UNITED KINGDOM|LONDON|ENGLAND|BRITISH/.test(upper)) {
    fields.issuingCountry = field('GB', 0.70, 'inferred');
  } else if (/UNITED STATES|USA/.test(upper)) {
    fields.issuingCountry = field('US', 0.70, 'inferred');
  }

  return { fields };
}

function extractTaxIdFields(text) {
  const fields = {};

  const ssnMatch = text.match(/\b(\d{3}-\d{2}-\d{4})\b/);
  if (ssnMatch) {
    fields.documentNumber = field(ssnMatch[1], 0.90, 'regex');
  }

  if (!fields.documentNumber) {
    const tinMatch = text.match(/\b(?:TIN|TAX\s*ID)[:\s]+([A-Z0-9\-]{6,15})\b/i);
    if (tinMatch) fields.documentNumber = field(tinMatch[1], 0.70, 'regex');
  }

  return { fields };
}

/**
 * Extract typed fields from OCR text.
 * Returns `{ fields }` where each field is `{ value, confidence, source }`.
 *
 * @param {string} text         Full OCR text
 * @param {string} documentType One of PASSPORT | NATIONAL_ID | BANK_STATEMENT | PROOF_OF_ADDRESS | TAX_ID
 * @param {string} mrzText      Optional second OCR pass on the bottom 25% (passport MRZ crop)
 */
export function extractFields(text, documentType, mrzText = '') {
  switch (documentType) {
    case 'PASSPORT':         return extractPassportFields(text, mrzText);
    case 'NATIONAL_ID':      return extractNationalIdFields(text);
    case 'BANK_STATEMENT':   return extractBankStatementFields(text);
    case 'PROOF_OF_ADDRESS': return extractProofOfAddressFields(text);
    case 'TAX_ID':           return extractTaxIdFields(text);
    default:
      return { fields: {} };
  }
}

/* ── validation ──────────────────────────────────────────────────────────── */
export function validateFields(fields, documentType) {
  const warnings = [];
  const now = new Date();

  if (fields.expiryDate) {
    const expiry = new Date(fields.expiryDate);
    if (!Number.isNaN(expiry.getTime()) && expiry < now) {
      warnings.push({ field: 'expiryDate', message: 'Document appears to be expired' });
    }
  }

  if (fields.issueDate) {
    const issued = new Date(fields.issueDate);
    if (!Number.isNaN(issued.getTime()) && issued > now) {
      warnings.push({ field: 'issueDate', message: 'Issue date is in the future' });
    }
  }

  if (documentType === 'PROOF_OF_ADDRESS' && fields.issueDate) {
    const issued = new Date(fields.issueDate);
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    if (!Number.isNaN(issued.getTime()) && issued < threeMonthsAgo) {
      warnings.push({ field: 'issueDate', message: 'Proof of address may be too old (over 3 months)' });
    }
  }

  if (documentType === 'TAX_ID' && fields.documentNumber) {
    const ssnRe = /^\d{3}-\d{2}-\d{4}$/;
    if (!ssnRe.test(fields.documentNumber) && /^\d{9}$/.test(fields.documentNumber.replace(/-/g, ''))) {
      warnings.push({ field: 'documentNumber', message: 'Tax ID format may be incorrect' });
    }
  }

  return { valid: warnings.length === 0, warnings };
}

/* ── top-level extraction ────────────────────────────────────────────────── */

/**
 * @param {File}     file           The uploaded document file
 * @param {string}   hintedType     Optional pre-selected document type
 * @param {Function} onStatusChange Optional status callback (status, progress)
 * @param {Object}   options        Optional: { languageHint: ISO-2 country code }
 */
export async function extractDocument(file, hintedType = null, onStatusChange = null, options = {}) {
  const { languageHint = null } = options;

  const notify = (status, progress = 0) => {
    if (onStatusChange) onStatusChange(status, progress);
  };

  const isPdfFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  const isImageFile = /\.(jpe?g|png|gif|webp|bmp|tiff?)$/i.test(file.name);

  notify('preprocessing', 0);

  // Use Arabic OCR for GCC national IDs and proof-of-address documents
  const needsArabic = ARABIC_DOC_TYPES.has(hintedType) && GCC_COUNTRIES.has(languageHint);
  const ocrLang = needsArabic ? 'eng+ara' : 'eng';

  let ocrResult;
  let preprocessedCanvas = null;

  if (isPdfFile) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const numPages = Math.min(pdf.numPages, 5);
      const pageTexts = [];
      let firstConfidence = 0;
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        const renderedCanvas = await renderPageToCanvas(page, 2.0);
        const { canvas: processedCanvas, dataUrl } = preprocessCanvas(renderedCanvas);
        if (i === 1) preprocessedCanvas = processedCanvas;
        notify('ocr', 0);
        const pageOcr = await runOcrWithOptions(
          dataUrl,
          ocrLang,
          {},
          i === 1 ? (pct) => notify('ocr', pct) : undefined,
        );
        if (i === 1) firstConfidence = pageOcr.confidence;
        pageTexts.push(pageOcr.text);
      }
      ocrResult = {
        text: pageTexts.join('\n\n--- PAGE BREAK ---\n\n'),
        confidence: firstConfidence,
        words: [],
      };
    } catch (err) {
      throw new Error(`OCR failed: ${err.message}`);
    }
  } else {
    let imageData = file;
    if (isImageFile) {
      try {
        const preprocessed = await imagePreprocess(file);
        imageData = preprocessed.dataUrl;
        preprocessedCanvas = preprocessed.canvas;
      } catch (_e) {
        imageData = file;
      }
    }
    notify('ocr', 0);
    try {
      ocrResult = await runOcrWithOptions(imageData, ocrLang, {}, (pct) => notify('ocr', pct));
    } catch (err) {
      throw new Error(`OCR failed: ${err.message}`);
    }
  }

  notify('extracting', 95);

  const { text, confidence } = ocrResult;
  const detection = detectDocumentType(text, hintedType);
  const documentType = detection.type;

  // For passports: run a second targeted OCR pass on the bottom 25% (MRZ zone)
  let mrzText = '';
  if (documentType === 'PASSPORT' && preprocessedCanvas) {
    try {
      const mrzCanvas = cropCanvas(
        preprocessedCanvas,
        0,
        preprocessedCanvas.height * 0.75,
        preprocessedCanvas.width,
        preprocessedCanvas.height * 0.25
      );
      const mrzOcr = await runOcrWithOptions(
        mrzCanvas.toDataURL('image/png'),
        'eng',
        { tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<' }
      );
      mrzText = mrzOcr.text;
    } catch (_e) {
      // MRZ crop OCR failed — full-text MRZ will be attempted in extractFields
    }
  }

  let extractedFields = {};
  if (documentType) {
    const extracted = extractFields(text, documentType, mrzText);
    extractedFields = extracted.fields;
  }

  // Convert to plain values for validateFields (which expects string values)
  const plainFields = Object.fromEntries(
    Object.entries(extractedFields).map(([k, v]) => [k, v?.value || ''])
  );
  const { valid, warnings } = validateFields(plainFields, documentType);

  const extractedCount = Object.values(extractedFields).filter((f) => f && f.value).length;
  const totalFields = Object.keys(extractedFields).length;

  return {
    documentType,
    extractedFields,
    confidence,
    validationStatus: {
      valid,
      warnings,
      checksumValid: valid,
      allRequiredFieldsExtracted: extractedCount > 0 && extractedCount >= Math.min(2, totalFields),
    },
    rawOcrText: text,
  };
}
